/**
 * Rotate animation utility functions for AnimatedItem interface
 * Provides rotating entrance and exit animations with customizable behavior
 */

import { interpolate, Easing } from 'remotion';

/**
 * Calculate rotate animation transform values
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @param startRotation - Starting rotation in degrees (default: -15)
 * @param endRotation - Ending rotation in degrees (default: 0)
 * @returns Transform values for current animation frame
 */
export function calculateRotateTransform(
  progress: number,
  direction: 'entrance' | 'exit',
  startRotation: number = -15,
  endRotation: number = 0
): { rotation: number; opacity: number } {
  // Validate inputs
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }

  // Use Remotion's interpolate with easing for smooth animation
  const easedProgress = direction === 'entrance' 
    ? interpolate(progress, [0, 1], [0, 1], {
        easing: Easing.out(Easing.cubic)
      })
    : interpolate(progress, [0, 1], [1, 0], {
        easing: Easing.in(Easing.cubic)
      });
  
  // Calculate rotation based on progress
  const rotation = startRotation + (endRotation - startRotation) * easedProgress;
  
  // Fade during rotation for smoother appearance
  const opacity = easedProgress;
  
  return { rotation, opacity };
}

/**
 * Get CSS transform for rotate animation with hardware acceleration
 * @param rotation - Current rotation in degrees
 * @param opacity - Current opacity value
 * @returns CSS style object for rotate animation
 */
export function getRotateAnimationStyle(rotation: number, opacity: number): React.CSSProperties {
  return {
    transform: `rotate3d(0, 0, 1, ${rotation}deg)`,
    opacity,
    willChange: 'transform, opacity',
    transformOrigin: 'center center',
  };
}

/**
 * Calculate rotate animation with default settings
 */
export function calculateRotateAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { rotation, opacity } = calculateRotateTransform(progress, direction);
  return getRotateAnimationStyle(rotation, opacity);
}